import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authStore } from '../api/client';
import { updateProfile, changePassword } from '../api/auth';
import { getPolicy, updatePolicy } from '../api/orgPolicy';
import { getOrgById, updateOrg, deleteOrg, listMembers, removeMember, listMyOrgs } from '../api/orgs';
import { INDUSTRY_SUGGESTIONS, getOrgBlueprintSettings, listPinnableVersions, updateOrgBlueprintSettings } from '../api/blueprints';
import { getMinAgentVersion, patchMinAgentVersion } from '../api/admin';
import {
  listWebhooks, createWebhook, deleteWebhook, testWebhook, getGreenhouseWebhookSecret,
} from '../api/webhooks';
import { listApiKeys, createApiKey, revokeApiKey } from '../api/apiKeys';
import { getNotificationPrefs, updateNotificationPrefs } from '../api/notifications';
import Avatar from '../components/ui/Avatar';
import { useToast } from '../components/ui/Toast';
import { getApiBaseUrl } from '../config/env';
import { formatRole } from '../utils/formatters';

// ---------------------------------------------------------------------------
// Local presentational helpers (single-use, kept inline)
// ---------------------------------------------------------------------------

const inputCls =
  'w-full border border-line rounded-lg px-3 py-2 text-sm text-ink bg-surface ' +
  'focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 ' +
  'placeholder:text-ink-faint';

function Field({ label, children, hint }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-ink-soft uppercase tracking-wide mb-1.5">
        {label}
      </label>
      {children}
      {hint && <p className="text-[11px] text-ink-faint mt-1">{hint}</p>}
    </div>
  );
}

function Section({ title, subtitle, children, danger }) {
  return (
    <div className={`bg-surface rounded-xl border p-6 ${danger ? 'border-rose-200' : 'border-line'}`}>
      <div className="mb-5">
        <h2 className={`font-heading text-base font-semibold ${danger ? 'text-rose-700' : 'text-ink'}`}>{title}</h2>
        {subtitle && <p className="text-xs text-ink-faint mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-teal-600"
      />
      <div>
        <p className="text-sm text-ink font-medium">{label}</p>
        {description && <p className="text-xs text-ink-faint">{description}</p>}
      </div>
    </label>
  );
}

function StatusMsg({ msg }) {
  if (!msg) return null;
  return (
    <p className={`text-xs mb-3 ${msg.ok ? 'text-green-600' : 'text-red-500'}`}>{msg.text}</p>
  );
}

function settingsNavSections(role) {
  const nav = [
    { id: 'profile', label: 'Profile' },
    { id: 'availability', label: 'My availability', href: '/settings/availability' },
  ];
  if (role === 'ORG_ADMIN' || role === 'SUPER_ADMIN') {
    nav.push({ id: 'organization', label: 'Organization' });
    nav.push({ id: 'policy', label: 'Integrity Policy' });
    nav.push({ id: 'questionBank', label: 'Question Bank' });
    nav.push({ id: 'aptitudeTests', label: 'Aptitude Tests', href: '/settings/aptitude-tests' });
    nav.push({ id: 'notifications', label: 'Notifications' });
    nav.push({ id: 'integrations', label: 'Integrations' });
    nav.push({ id: 'danger', label: 'Danger Zone', danger: true });
  }
  if (role === 'SUPER_ADMIN') {
    const idx = nav.findIndex((x) => x.id === 'organization');
    if (idx >= 0) nav.splice(idx + 1, 0, { id: 'platform', label: 'Platform' });
  }
  return nav;
}

// ---------------------------------------------------------------------------
// SettingsPage - Phase 10 left-nav layout
// ---------------------------------------------------------------------------
export default function SettingsPage() {
  const navigate = useNavigate();
  const user = authStore.user || {};
  const [orgId, setOrgId] = useState(user?.orgId || null);
  const role = user.role || authStore.role;

  const navSections = useMemo(() => settingsNavSections(role), [role]);

  const [activeSection, setActiveSection] = useState('profile');

  useEffect(() => {
    if (orgId) return;
    if (!user?.email) return;
    listMyOrgs(user.email)
      .then((res) => {
        const orgs = res.data;
        if (Array.isArray(orgs) && orgs.length > 0) {
          setOrgId(orgs[0].id);
        }
      })
      .catch(() => {});
  }, [user?.email]);

  // Per-section dirty flags - the navigation blocker only fires when there is
  // actually unsaved work somewhere on the page.
  const [dirty, setDirty] = useState({
    profile: false, password: false, organization: false, policy: false, notifications: false,
  });
  const anyDirty = useMemo(() => Object.values(dirty).some(Boolean), [dirty]);

  // Warn on full-page unload / refresh when there is unsaved work.
  // (We previously also used react-router's useBlocker for in-app navigation,
  // but that hook requires a data router which we do not use.)
  useEffect(() => {
    if (!anyDirty) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [anyDirty]);

  useEffect(() => {
    if (navSections.some((s) => s.id === activeSection)) return;
    setActiveSection('profile');
  }, [navSections, activeSection]);

  return (
    <div className="page-pad page-y max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Settings</h1>
        <p className="text-sm text-ink-faint mt-1">Manage your account, organization, and integrations</p>
      </div>

      <div className="grid grid-cols-12 gap-5 sm:gap-6">
        <nav className="col-span-12 md:col-span-3">
          <div className="card p-2 md:sticky md:top-6">
            {navSections.map((s) => {
              const active = s.id === activeSection;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    if (s.href) {
                      navigate(s.href);
                      return;
                    }
                    setActiveSection(s.id);
                  }}
                  className={`w-full text-left text-xs font-semibold px-3 py-2.5 rounded-xl mb-1 transition ${
                    active
                      ? s.danger
                        ? 'bg-rose-50 text-rose-700 font-semibold'
                        : 'bg-teal-50 text-teal-800 font-semibold shadow-xs'
                      : 'text-ink-soft hover:bg-canvas hover:text-ink'
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </nav>

        <div className="col-span-12 md:col-span-9 space-y-6">
          {activeSection === 'profile' && (
            <ProfileSection
              user={user}
              onProfileDirty={(v) => setDirty((d) => ({ ...d, profile: v }))}
              onPasswordDirty={(v) => setDirty((d) => ({ ...d, password: v }))}
            />
          )}
          {activeSection === 'organization' && (
            <OrganizationSection
              orgId={orgId}
              onDirtyChange={(v) => setDirty((d) => ({ ...d, organization: v }))}
            />
          )}
          {activeSection === 'platform' && role === 'SUPER_ADMIN' && <PlatformSection />}
          {activeSection === 'policy' && (
            <PolicySection orgId={orgId}
              onDirtyChange={(v) => setDirty((d) => ({ ...d, policy: v }))} />
          )}
          {activeSection === 'questionBank' && <QuestionBankSection />}
          {activeSection === 'notifications' && (
            <NotificationsSection
              onDirtyChange={(v) => setDirty((d) => ({ ...d, notifications: v }))} />
          )}
          {activeSection === 'integrations' && <IntegrationsSection orgId={orgId} />}
          {activeSection === 'danger' && <DangerZoneSection orgId={orgId} />}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Profile + Password (preserved logic from previous version)
// ===========================================================================
function ProfileSection({ user, onProfileDirty, onPasswordDirty }) {
  const [profile, setProfile] = useState({
    fullName:  user.fullName  || '',
    avatarUrl: user.avatarUrl || '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg,    setProfileMsg]    = useState(null);

  useEffect(() => {
    const isDirty =
      profile.fullName  !== (user.fullName  || '') ||
      profile.avatarUrl !== (user.avatarUrl || '');
    onProfileDirty(isDirty);
  }, [profile, user.fullName, user.avatarUrl, onProfileDirty]);

  async function handleProfileSave(e) {
    e.preventDefault();
    setProfileSaving(true); setProfileMsg(null);
    try {
      const res = await updateProfile({
        fullName:  profile.fullName.trim()  || undefined,
        avatarUrl: profile.avatarUrl.trim() || undefined,
      });
      authStore.user = res.data;
      setProfileMsg({ ok: true, text: 'Profile updated.' });
      onProfileDirty(false);
    } catch (err) {
      setProfileMsg({ ok: false, text: err.response?.data?.message || 'Update failed.' });
    } finally { setProfileSaving(false); }
  }

  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving,  setPwSaving]  = useState(false);
  const [pwMsg,     setPwMsg]     = useState(null);

  useEffect(() => { onPasswordDirty(!!(pw.current || pw.next || pw.confirm)); },
    [pw, onPasswordDirty]);

  async function handlePasswordSave(e) {
    e.preventDefault();
    if (pw.next !== pw.confirm) { setPwMsg({ ok: false, text: 'New passwords do not match.' }); return; }
    if (pw.next.length < 8)     { setPwMsg({ ok: false, text: 'Password must be at least 8 characters.' }); return; }
    setPwSaving(true); setPwMsg(null);
    try {
      await changePassword({ currentPassword: pw.current, newPassword: pw.next });
      setPw({ current: '', next: '', confirm: '' });
      setPwMsg({ ok: true, text: 'Password changed successfully.' });
    } catch (err) {
      setPwMsg({ ok: false, text: err.response?.data?.message || 'Password change failed.' });
    } finally { setPwSaving(false); }
  }

  return (
    <>
      <Section title="Account" subtitle="Your login details">
        <div className="flex items-center gap-4">
          <Avatar name={profile.fullName || user.email || '?'} email={user.email || ''} size="lg" />
          <div>
            <p className="font-semibold text-ink">{profile.fullName || '-'}</p>
            <p className="text-sm text-ink-faint">{user.email}</p>
            <span className="chip-teal mt-1">
              {formatRole(user.role || 'INTERVIEWER')}
            </span>
          </div>
        </div>
      </Section>

      <Section title="Profile" subtitle="Update your display name and avatar">
        <form onSubmit={handleProfileSave}>
          <Field label="Full Name">
            <input className={inputCls} value={profile.fullName}
              onChange={(e) => setProfile((p) => ({ ...p, fullName: e.target.value }))} placeholder="Jane Smith" />
          </Field>
          <Field label="Avatar URL">
            <input className={inputCls} value={profile.avatarUrl}
              onChange={(e) => setProfile((p) => ({ ...p, avatarUrl: e.target.value }))} placeholder="https://..." />
          </Field>
          <StatusMsg msg={profileMsg} />
          <button type="submit" disabled={profileSaving}
            className="btn btn-primary text-xs font-semibold px-5 py-2 disabled:opacity-60">
            {profileSaving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </Section>

      <Section title="Password" subtitle="Must be at least 8 characters">
        <form onSubmit={handlePasswordSave}>
          <Field label="Current Password">
            <input type="password" className={inputCls} value={pw.current}
              onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} autoComplete="current-password" />
          </Field>
          <Field label="New Password">
            <input type="password" className={inputCls} value={pw.next}
              onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} autoComplete="new-password" />
          </Field>
          <Field label="Confirm New Password">
            <input type="password" className={inputCls} value={pw.confirm}
              onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} autoComplete="new-password" />
          </Field>
          <StatusMsg msg={pwMsg} />
          <button type="submit" disabled={pwSaving}
            className="bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white text-sm font-medium px-5 py-2 rounded-lg transition">
            {pwSaving ? 'Updating...' : 'Change Password'}
          </button>
        </form>
      </Section>
    </>
  );
}

// ===========================================================================
// Platform (SUPER_ADMIN)
// ===========================================================================
function PlatformSection() {
  const [version, setVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    getMinAgentVersion()
      .then((res) => {
        const d = res.data;
        const v = d?.minVersion ?? d?.minimumVersion ?? d?.version ?? '';
        setVersion(typeof v === 'string' ? v : String(v ?? ''));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      await patchMinAgentVersion({ minimumVersion: version.trim() });
      setMsg({ ok: true, text: 'Saved.' });
    } catch (err) {
      setMsg({
        ok: false,
        text: err.response?.data?.message || 'Could not save. The PATCH /api/admin/min-version endpoint may be unavailable.',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section title="Platform" subtitle="Global agent requirements (super admin)">
      {loading ? (
        <p className="text-xs text-slate-400">Loading…</p>
      ) : (
        <form onSubmit={handleSave}>
          <Field label="Minimum agent version" hint="Optional — enforced by the backend when set.">
            <input
              className={inputCls}
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. 1.4.0"
            />
          </Field>
          <StatusMsg msg={msg} />
          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary text-xs font-semibold px-5 py-2 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      )}
    </Section>
  );
}

// ===========================================================================
// Organization members (ORG_ADMIN + SUPER_ADMIN)
// ===========================================================================
function OrganizationMembers({ orgId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    listMembers(orgId)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.members ?? []);
        setMembers(list);
      })
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [orgId]);

  async function handleRemove(email) {
    if (!window.confirm(`Remove ${email} from this organization?`)) return;
    try {
      await removeMember(orgId, email);
      setMembers((prev) => prev.filter((m) => (m.email || m.memberEmail) !== email));
    } catch (err) {
      alert(err.response?.data?.message || 'Remove failed.');
    }
  }

  if (!orgId) return null;

  return (
    <div className="mt-8 pt-6 border-t border-slate-100">
      <h3 className="text-sm font-semibold text-slate-800 mb-3">Members</h3>
      {loading ? (
        <p className="text-xs text-slate-400">Loading members…</p>
      ) : (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
          {members.length === 0 ? (
            <p className="p-3 text-xs text-slate-500">No members listed.</p>
          ) : (
            members.map((m) => {
              const email = m.email || m.memberEmail;
              const r = (m.role || 'MEMBER').toUpperCase();
              const canRemove = r !== 'OWNER';
              return (
                <div key={email} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm bg-white">
                  <span className="text-slate-700 truncate min-w-0">{email}</span>
                  <span className="text-[10px] font-semibold uppercase text-slate-500 shrink-0">{r}</span>
                  {canRemove ? (
                    <button
                      type="button"
                      className="text-xs font-medium text-red-600 hover:text-red-700 shrink-0"
                      onClick={() => handleRemove(email)}
                    >
                      Remove
                    </button>
                  ) : (
                    <span className="text-[10px] text-slate-400 shrink-0">—</span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Organization (name + logo)
// ===========================================================================
function OrganizationSection({ orgId, onDirtyChange }) {
  const [org,     setOrg]     = useState(null);
  const [form,    setForm]    = useState({ name: '', logoUrl: '' });
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    getOrgById(orgId)
      .then((res) => {
        setOrg(res.data);
        setForm({ name: res.data.name || '', logoUrl: res.data.logoUrl || '' });
      })
      .catch((err) => setMsg({ ok: false, text: err.response?.data?.message || 'Failed to load org.' }))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    if (!org) return;
    const isDirty = form.name !== (org.name || '') || form.logoUrl !== (org.logoUrl || '');
    onDirtyChange(isDirty);
  }, [form, org, onDirtyChange]);

  async function handleSave(e) {
    e.preventDefault();
    if (!orgId) return;
    setSaving(true); setMsg(null);
    try {
      const res = await updateOrg(orgId, { name: form.name, logoUrl: form.logoUrl });
      setOrg(res.data);
      setMsg({ ok: true, text: 'Organization updated.' });
      onDirtyChange(false);
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.message || 'Save failed.' });
    } finally { setSaving(false); }
  }

  if (!orgId) {
    return <Section title="Organization"><p className="text-xs text-slate-400">You are not a member of any organization.</p></Section>;
  }

  return (
    <Section title="Organization" subtitle="Your org's display name, logo, and members">
      {loading ? <p className="text-xs text-slate-400">Loading…</p> : (
        <>
          <form onSubmit={handleSave}>
            <Field label="Slug" hint="URL identifier for your org. Cannot be changed.">
              <input className={inputCls} value={org?.slug || ''} disabled />
            </Field>
            <Field label="Name">
              <input className={inputCls} value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Acme Corp" />
            </Field>
            <Field label="Logo URL" hint="A direct link to your logo image (PNG/SVG, ~200×200).">
              <input className={inputCls} value={form.logoUrl}
                onChange={(e) => setForm((f) => ({ ...f, logoUrl: e.target.value }))} placeholder="https://..." />
            </Field>
            {form.logoUrl && (
              <div className="mb-4 flex items-center gap-3">
                <span className="text-xs text-slate-500">Preview:</span>
                <img src={form.logoUrl} alt="Org logo" className="h-12 w-12 rounded border border-slate-200 object-cover" />
              </div>
            )}
            <StatusMsg msg={msg} />
            <button type="submit" disabled={saving}
              className="btn btn-primary text-xs font-semibold px-5 py-2 disabled:opacity-60">
              {saving ? 'Saving...' : 'Save Organization'}
            </button>
          </form>
          <OrgBlueprintSettings orgId={orgId} />
          <OrganizationMembers orgId={orgId} />
        </>
      )}
    </Section>
  );
}

// ===========================================================================
// Blueprint Phase 12D: org industry + optional default interview blueprint.
// The default is only used for roles it is compatible with; otherwise the
// Samastix general blueprint applies (see BlueprintResolver on the server).
// ===========================================================================
function OrgBlueprintSettings({ orgId }) {
  const [settings, setSettings] = useState(null);
  const [versions, setVersions] = useState([]);
  const [form, setForm] = useState({ industry: '', defaultBlueprintVersionId: '' });
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    getOrgBlueprintSettings(orgId).then((res) => {
      setSettings(res.data);
      setForm({ industry: res.data.industry || '', defaultBlueprintVersionId: res.data.defaultBlueprintVersionId || '' });
    }).catch(() => setSettings(null));
    listPinnableVersions(orgId).then(setVersions).catch(() => setVersions([]));
  }, [orgId]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const res = await updateOrgBlueprintSettings(orgId, {
        industry: form.industry,
        defaultBlueprintVersionId: form.defaultBlueprintVersionId || null,
        clearDefault: !form.defaultBlueprintVersionId,
      });
      setSettings(res.data);
      setMsg({ ok: true, text: 'Interview settings saved.' });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return null;
  const pinnedMissing = settings.defaultBlueprintVersionId
    && !versions.some((v) => v.versionId === settings.defaultBlueprintVersionId);
  return (
    <form onSubmit={save} className="mt-6 pt-5 border-t border-line">
      <h3 className="font-heading text-sm font-semibold text-ink mb-3">Interview blueprints</h3>
      <Field label="Industry" hint="Used to match blueprints to your roles.">
        <input className={inputCls} list="org-industries" value={form.industry}
          onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))} placeholder="e.g. fintech" />
        <datalist id="org-industries">{INDUSTRY_SUGGESTIONS.map((i) => <option key={i} value={i} />)}</datalist>
      </Field>
      <Field label="Default blueprint" hint="Used for jobs without a pinned blueprint, only when the role matches it. Other roles use the Samastix general blueprint.">
        <select className={inputCls} value={form.defaultBlueprintVersionId}
          onChange={(e) => setForm((f) => ({ ...f, defaultBlueprintVersionId: e.target.value }))}>
          <option value="">None</option>
          {pinnedMissing && (
            <option value={settings.defaultBlueprintVersionId}>
              {settings.defaultBlueprintName} · v{settings.defaultBlueprintVersionNo}
            </option>
          )}
          {versions.map((v) => <option key={v.versionId} value={v.versionId}>{v.label}</option>)}
        </select>
      </Field>
      <StatusMsg msg={msg} />
      <button type="submit" disabled={saving} className="btn btn-secondary text-xs font-semibold px-5 py-2 disabled:opacity-60">
        {saving ? 'Saving...' : 'Save interview settings'}
      </button>
    </form>
  );
}

// ===========================================================================
// Integrity Policy
// ===========================================================================
// I-P0-B: per-field truth about what the server does with each setting.
//  - Allowed / blocked process lists: APPLIED by the server integrity pipeline when it
//    classifies process signals (allowed wins over blocked; exact name / token match).
//  - Notify on high-severity signals: APPLIED; gates the FINDING_CRITICAL webhook, which
//    only fires for a server-classified HIGH signal.
//  - Auto-pause / auto-end thresholds: NOT EVALUATED. They were 0-100 score thresholds and
//    the server no longer produces a numeric score; no interview is ever paused or ended
//    automatically. They stay visible read-only and are saved back unchanged.
const LISTS_AND_NOTIFY_APPLIED = true;
const THRESHOLDS_EVALUATED = false;

function PolicySection({ orgId, onDirtyChange }) {
  const initial = { autoPauseThreshold: '', autoEndThreshold: '', notifyOnCritical: true, allowedProcesses: '', blockedProcesses: '' };
  const [policy,  setPolicy]  = useState(initial);
  const [loaded,  setLoaded]  = useState(initial);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    getPolicy(orgId)
      .then((res) => {
        const d = res.data;
        const next = {
          autoPauseThreshold: d.autoPauseThreshold ?? '',
          autoEndThreshold:   d.autoEndThreshold   ?? '',
          notifyOnCritical:   d.notifyOnCritical   ?? true,
          allowedProcesses:   (d.allowedProcesses || []).join(', '),
          blockedProcesses:   (d.blockedProcesses || []).join(', '),
        };
        setPolicy(next); setLoaded(next);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    onDirtyChange(JSON.stringify(policy) !== JSON.stringify(loaded));
  }, [policy, loaded, onDirtyChange]);

  async function handleSave(e) {
    e.preventDefault();
    if (!orgId || !LISTS_AND_NOTIFY_APPLIED) return;
    setSaving(true); setMsg(null);
    try {
      const splitCSV = (s) => s.split(',').map((v) => v.trim()).filter(Boolean);
      await updatePolicy(orgId, {
        // Thresholds are not editable (not evaluated); the stored values are sent back unchanged.
        autoPauseThreshold: loaded.autoPauseThreshold !== '' ? Number(loaded.autoPauseThreshold) : null,
        autoEndThreshold:   loaded.autoEndThreshold   !== '' ? Number(loaded.autoEndThreshold)   : null,
        notifyOnCritical:   policy.notifyOnCritical,
        allowedProcesses:   splitCSV(policy.allowedProcesses),
        blockedProcesses:   splitCSV(policy.blockedProcesses),
      });
      setLoaded(policy);
      setMsg({ ok: true, text: 'Policy saved.' });
      onDirtyChange(false);
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.message || 'Save failed.' });
    } finally { setSaving(false); }
  }

  if (!orgId) {
    return <Section title="Integrity Policy"><p className="text-xs text-slate-400">No organization to configure.</p></Section>;
  }

  return (
    <Section title="Integrity Policy" subtitle="Process allow/block lists and high-severity notifications for your org's sessions">
      <div role="note" className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
        <span className="font-semibold">What is applied today.</span> Process lists change how the server
        classifies process signals (an allowed process is context only, a blocked process is a high
        review signal; allowed wins if a name is on both lists). The notification toggle controls the
        webhook for server-classified high-severity signals. Signals are for human review only: no
        interview is paused or ended automatically.
      </div>
      {loading ? <p className="text-xs text-slate-400">Loading policy…</p> : (
        <form onSubmit={handleSave}>
          <fieldset disabled={!LISTS_AND_NOTIFY_APPLIED} className="contents">
          {!THRESHOLDS_EVALUATED && (
            <p className="text-[11px] text-amber-800 mb-2">
              <span className="font-semibold">Not evaluated:</span> the score thresholds below are kept
              for reference only. The server no longer produces a numeric integrity score, so they
              are never applied.
            </p>
          )}
          <fieldset disabled={!THRESHOLDS_EVALUATED} className="contents">
          <div className="grid grid-cols-2 gap-4 mb-4 opacity-60">
            <Field label="Auto-Pause threshold (score)">
              <input type="number" min={0} max={100} className={inputCls}
                value={policy.autoPauseThreshold}
                onChange={(e) => setPolicy((p) => ({ ...p, autoPauseThreshold: e.target.value }))}
                placeholder="e.g. 50" />
            </Field>
            <Field label="Auto-End threshold (score)">
              <input type="number" min={0} max={100} className={inputCls}
                value={policy.autoEndThreshold}
                onChange={(e) => setPolicy((p) => ({ ...p, autoEndThreshold: e.target.value }))}
                placeholder="e.g. 80" />
            </Field>
          </div>
          </fieldset>
          <Field label="Allowed processes (comma-separated)">
            <textarea rows={2} className={inputCls} value={policy.allowedProcesses}
              onChange={(e) => setPolicy((p) => ({ ...p, allowedProcesses: e.target.value }))} />
          </Field>
          <Field label="Blocked processes (comma-separated)">
            <textarea rows={2} className={inputCls} value={policy.blockedProcesses}
              onChange={(e) => setPolicy((p) => ({ ...p, blockedProcesses: e.target.value }))} />
          </Field>
          <Toggle checked={policy.notifyOnCritical}
            onChange={(v) => setPolicy((p) => ({ ...p, notifyOnCritical: v }))}
            label="Notify on high-severity signals (webhook)" />
          <StatusMsg msg={msg} />
          <button type="submit" disabled={saving}
            className="btn btn-primary text-xs font-semibold px-5 py-2 disabled:opacity-60 mt-3">
            {saving ? 'Saving…' : 'Save Policy'}
          </button>
          </fieldset>
        </form>
      )}
    </Section>
  );
}

// ===========================================================================
// Question Bank Defaults (placeholder)
// ===========================================================================
function QuestionBankSection() {
  return (
    <Section title="Question Bank Defaults" subtitle="Default difficulty and category preferences for new question sets">
      <p className="text-xs text-slate-400">
        Question bank defaults will be configurable here once the question bank service ships.
        For now, defaults are inferred per-session at creation time.
      </p>
    </Section>
  );
}

// ===========================================================================
// Notification Preferences
// ===========================================================================
function NotificationsSection({ onDirtyChange }) {
  const [prefs,  setPrefs]  = useState(null);
  const [loaded, setLoaded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState(null);

  useEffect(() => {
    getNotificationPrefs()
      .then((res) => { setPrefs(res.data); setLoaded(res.data); })
      .catch((err) => setMsg({ ok: false, text: err.response?.data?.message || 'Failed to load preferences.' }));
  }, []);

  useEffect(() => {
    if (!prefs || !loaded) return;
    onDirtyChange(JSON.stringify(prefs) !== JSON.stringify(loaded));
  }, [prefs, loaded, onDirtyChange]);

  async function handleSave() {
    if (!prefs) return;
    setSaving(true); setMsg(null);
    try {
      const res = await updateNotificationPrefs(prefs);
      setPrefs(res.data); setLoaded(res.data);
      setMsg({ ok: true, text: 'Preferences saved.' });
      onDirtyChange(false);
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.message || 'Save failed.' });
    } finally { setSaving(false); }
  }

  return (
    <Section title="Notifications" subtitle="Choose what we email you about">
      {!prefs ? <p className="text-xs text-slate-400">Loading…</p> : (
        <>
          <Toggle checked={prefs.emailOnCritical}
            onChange={(v) => setPrefs((p) => ({ ...p, emailOnCritical: v }))}
            label="Email on CRITICAL findings"
            description="Send an email the moment a session produces a CRITICAL-severity finding." />
          <Toggle checked={prefs.emailOnSessionEnd}
            onChange={(v) => setPrefs((p) => ({ ...p, emailOnSessionEnd: v }))}
            label="Email when a session ends"
            description="Receive a summary email when an interview session you scheduled wraps up." />
          <Toggle checked={prefs.emailWeeklyDigest}
            onChange={(v) => setPrefs((p) => ({ ...p, emailWeeklyDigest: v }))}
            label="Weekly digest email"
            description="A Monday-morning recap of last week's sessions and findings." />
          <Toggle checked={prefs.desktopAlerts}
            onChange={(v) => setPrefs((p) => ({ ...p, desktopAlerts: v }))}
            label="Desktop alerts (in proctor app)"
            description="Show toast notifications in the desktop proctor when alerts fire." />
          <StatusMsg msg={msg} />
          <button type="button" onClick={handleSave} disabled={saving}
            className="btn btn-primary text-xs font-semibold px-5 py-2 disabled:opacity-60 mt-3">
            {saving ? 'Saving…' : 'Save Preferences'}
          </button>
        </>
      )}
    </Section>
  );
}

// ===========================================================================
// Integrations (API keys, ATS webhooks, interview delay, outbound webhooks)
// ===========================================================================

const API_BASE_URL = getApiBaseUrl();

function formatKeyCreatedAt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

function IntegrationsSection({ orgId }) {
  const toast = useToast();

  const empty = { name: '', url: '', secret: '', events: 'FINDING_CRITICAL,SESSION_ENDED', enabled: true };
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding,  setAdding]  = useState(false);
  const [form,    setForm]    = useState(empty);
  const [msg,     setMsg]     = useState(null);
  const [busyId,  setBusyId]  = useState(null);

  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [keysLoading, setKeysLoading] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [minAtsScore, setMinAtsScore] = useState(75);
  const [atsScoreSaving, setAtsScoreSaving] = useState(false);
  const [interviewDelay, setInterviewDelay] = useState(24);
  const [delaySaving, setDelaySaving] = useState(false);
  const [sendCandidateFeedback, setSendCandidateFeedback] = useState(false);
  const [feedbackDelayHours, setFeedbackDelayHours] = useState(48);
  const [feedbackDelaySaving, setFeedbackDelaySaving] = useState(false);

  const greenhouseWebhookUrl = orgId ? `${API_BASE_URL}/v1/webhooks/greenhouse/${orgId}` : '';
  // Greenhouse signing secret (P0-07): fetched on demand, never cached or logged.
  const [ghSecret, setGhSecret] = useState(null);
  const [ghSecretBusy, setGhSecretBusy] = useState(false);
  const [ghSecretError, setGhSecretError] = useState('');

  async function revealGreenhouseSecret() {
    if (!orgId) return;
    setGhSecretBusy(true);
    setGhSecretError('');
    try {
      const res = await getGreenhouseWebhookSecret(orgId);
      setGhSecret(res.data?.secretKey || null);
    } catch (err) {
      // api/client rejects with { status, data, message } (no axios err.response).
      const status = err.status ?? err.response?.status;
      if (status === 503) setGhSecretError('ATS webhooks are not enabled on this server yet. Contact Samastix support.');
      else if (status === 403) setGhSecretError('Only organization owners and admins can view the webhook secret.');
      else setGhSecretError(err.data?.message || 'Could not load the webhook secret.');
    } finally {
      setGhSecretBusy(false);
    }
  }

  function refreshWebhooks() {
    if (!orgId) return;
    setLoading(true);
    listWebhooks(orgId)
      .then((res) => setList(res.data || []))
      .catch((err) => setMsg({ ok: false, text: err.response?.data?.message || 'Failed to load.' }))
      .finally(() => setLoading(false));
  }

  function refreshApiKeys() {
    if (!orgId) return;
    setKeysLoading(true);
    listApiKeys(orgId)
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setApiKeys(rows);
      })
      .catch(() => setApiKeys([]))
      .finally(() => setKeysLoading(false));
  }

  useEffect(() => {
    if (!orgId) return;
    getOrgById(orgId)
      .then((res) => {
        const data = res.data || {};
        const h = data.interviewDelayHours;
        setInterviewDelay(typeof h === 'number' && !Number.isNaN(h) ? h : 24);
        if (data.minAtsScore !== undefined) {
          const m = data.minAtsScore;
          setMinAtsScore(typeof m === 'number' && !Number.isNaN(m) ? m : 75);
        }
        setSendCandidateFeedback(!!data.sendCandidateFeedback);
        const fd = data.feedbackDelayHours;
        setFeedbackDelayHours(typeof fd === 'number' && !Number.isNaN(fd) ? fd : 48);
      })
      .catch(() => {});
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    refreshWebhooks();
    refreshApiKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- org-scoped reload only
  }, [orgId]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!orgId) return;
    setAdding(true); setMsg(null);
    try {
      await createWebhook(orgId, form);
      setForm(empty);
      setMsg({ ok: true, text: 'Webhook added.' });
      refreshWebhooks();
    } catch (err) {
      // Shows backend reasons such as INVALID_WEBHOOK_URL (P0-08).
      setMsg({ ok: false, text: err.data?.message || err.response?.data?.message || 'Create failed.' });
    } finally { setAdding(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this webhook?')) return;
    setBusyId(id);
    try { await deleteWebhook(orgId, id); refreshWebhooks(); }
    catch (err) { setMsg({ ok: false, text: err.data?.message || err.response?.data?.message || 'Delete failed.' }); }
    finally { setBusyId(null); }
  }

  async function handleTest(id) {
    setBusyId(id);
    try {
      const res = await testWebhook(orgId, id);
      setMsg({ ok: true, text: `Test sent. Last status: ${res.data.lastStatus}` });
      setList((rows) => rows.map((r) => (r.id === id ? res.data : r)));
    } catch (err) {
      setMsg({ ok: false, text: err.data?.message || err.response?.data?.message || 'Test failed.' });
    } finally { setBusyId(null); }
  }

  async function handleRevokeKey(keyId) {
    if (!orgId) return;
    if (!window.confirm('Revoke this API key? It will stop working immediately.')) return;
    try {
      await revokeApiKey(orgId, keyId);
      toast.success('Key revoked');
      refreshApiKeys();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Revoke failed');
    }
  }

  async function handleGenerateKey() {
    if (!orgId) return;
    const name = newKeyName.trim();
    if (!name) {
      toast.error('Enter a name for the key');
      return;
    }
    setGeneratingKey(true);
    try {
      const res = await createApiKey(orgId, name);
      setCreatedKey(res.data?.key ?? null);
      setNewKeyName('');
      refreshApiKeys();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not create key');
    } finally {
      setGeneratingKey(false);
    }
  }

  async function handleSaveMinAtsScore(e) {
    e.preventDefault();
    if (!orgId) return;
    setAtsScoreSaving(true);
    try {
      await updateOrg(orgId, { minAtsScore: Number(minAtsScore) });
      toast.success('Screening threshold saved');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setAtsScoreSaving(false);
    }
  }

  async function handleSaveInterviewDelay(e) {
    e.preventDefault();
    if (!orgId) return;
    setDelaySaving(true);
    try {
      await updateOrg(orgId, { interviewDelayHours: Number(interviewDelay) });
      toast.success('Interview delay saved');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setDelaySaving(false);
    }
  }

  async function handleSendCandidateFeedbackChange(enabled) {
    if (!orgId) return;
    setSendCandidateFeedback(enabled);
    try {
      await updateOrg(orgId, { sendCandidateFeedback: enabled });
      toast.success(enabled ? 'Candidate feedback enabled' : 'Candidate feedback disabled');
    } catch (err) {
      setSendCandidateFeedback(!enabled);
      toast.error(err.response?.data?.message || err.message || 'Update failed');
    }
  }

  async function handleFeedbackDelayChange(e) {
    const hours = Number(e.target.value);
    if (!orgId || Number.isNaN(hours)) return;
    setFeedbackDelayHours(hours);
    setFeedbackDelaySaving(true);
    try {
      await updateOrg(orgId, { feedbackDelayHours: hours });
      toast.success('Feedback delay saved');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setFeedbackDelaySaving(false);
    }
  }

  async function copyText(text, label) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label ? `${label} copied` : 'Copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  }

  if (!orgId) {
    return <Section title="Integrations"><p className="text-xs text-slate-400">No organization to configure.</p></Section>;
  }

  return (
    <>
      <Section
        title="API Keys"
        subtitle="Allow your systems to submit candidates directly via the Samastix REST API."
      >
        {createdKey && (
          <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <p className="font-medium text-amber-900 mb-2">Copy this key now — it will never be shown again.</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <code className="block flex-1 break-all rounded bg-white/80 border border-amber-200 px-3 py-2 text-xs font-mono text-slate-800">
                {createdKey}
              </code>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => copyText(createdKey, 'Key')}
                  className="text-xs font-medium px-3 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700"
                >
                  Copy
                </button>
                <button
                  type="button"
                  onClick={() => setCreatedKey(null)}
                  className="text-xs font-medium px-3 py-2 rounded-lg border border-amber-400 text-amber-900 hover:bg-amber-100"
                >
                  Done, I copied it
                </button>
              </div>
            </div>
          </div>
        )}

        {keysLoading ? (
          <p className="text-xs text-slate-400">Loading keys…</p>
        ) : apiKeys.length === 0 ? (
          <p className="text-xs text-slate-400 mb-4">No API keys yet.</p>
        ) : (
          <div className="overflow-x-auto -mx-2 mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="py-2 px-2">Name</th>
                  <th className="py-2 px-2">Key Prefix</th>
                  <th className="py-2 px-2">Created</th>
                  <th className="py-2 px-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((k) => (
                  <tr key={k.id} className="border-b border-slate-50 last:border-0">
                    <td className="py-2 px-2 text-slate-700">{k.name}</td>
                    <td className="py-2 px-2 font-mono text-xs text-slate-600 max-w-[200px] truncate" title={k.keyPrefix}>
                      {k.keyPrefix}
                    </td>
                    <td className="py-2 px-2 text-slate-500">{formatKeyCreatedAt(k.createdAt)}</td>
                    <td className="py-2 px-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleRevokeKey(k.id)}
                        className="text-xs font-medium text-red-600 hover:text-red-700"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Generate new key
            </label>
            <input
              className={inputCls}
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="Key name, e.g. HR Portal"
            />
          </div>
          <button
            type="button"
            disabled={generatingKey}
            onClick={handleGenerateKey}
            className="btn btn-primary text-xs font-semibold px-5 py-2 disabled:opacity-60 shrink-0 h-[38px]"
          >
            {generatingKey ? 'Generating…' : 'Generate Key'}
          </button>
        </div>
      </Section>

      <Section
        title="ATS Integrations"
        subtitle="Connect Greenhouse so candidates are invited automatically when moved to your interview stage."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-line bg-canvas/60 p-4">
            <h3 className="text-sm font-semibold text-ink mb-1">Greenhouse</h3>
            <p className="text-xs text-ink-faint mb-3">
              In Greenhouse → Settings → Dev Center → Web Hooks, add this URL and set the trigger to your Technical
              Interview stage.
            </p>
            <input className={inputCls} readOnly value={greenhouseWebhookUrl} />
            <button
              type="button"
              onClick={() => copyText(greenhouseWebhookUrl, 'URL')}
              className="mt-2 text-xs font-semibold text-teal-600 hover:text-teal-700"
            >
              Copy URL
            </button>
            <div className="mt-4 pt-3 border-t border-line">
              <p className="text-xs font-semibold text-ink mb-1">Secret key (required)</p>
              <p className="text-xs text-ink-faint mb-2 leading-relaxed">
                Paste this into the Secret Key field of the Greenhouse web hook. Samastix rejects any webhook
                whose Signature header does not verify with it.
              </p>
              {ghSecret ? (
                <>
                  <input className={`${inputCls} font-mono`} readOnly value={ghSecret} />
                  <div className="mt-2 flex gap-3">
                    <button
                      type="button"
                      onClick={() => copyText(ghSecret, 'Secret key')}
                      className="text-xs font-semibold text-teal-600 hover:text-teal-700"
                    >
                      Copy secret
                    </button>
                    <button
                      type="button"
                      onClick={() => setGhSecret(null)}
                      className="text-xs font-semibold text-ink-faint hover:text-ink"
                    >
                      Hide
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={revealGreenhouseSecret}
                  disabled={ghSecretBusy || !orgId}
                  className="text-xs font-semibold text-teal-600 hover:text-teal-700 disabled:opacity-60"
                >
                  {ghSecretBusy ? 'Loading…' : 'Reveal secret key'}
                </button>
              )}
              {ghSecretError && <p className="mt-2 text-xs text-rose-700">{ghSecretError}</p>}
            </div>
            <p className="mt-3 text-xs text-ink-faint leading-relaxed">
              Note: ATS scoring is skipped for webhook-triggered candidates (they are already pre-screened by your ATS
              stage). Use POST /v1/apply for resume-based screening.
            </p>
          </div>
          <div className="rounded-lg border border-line bg-canvas/60 p-4">
            <h3 className="text-sm font-semibold text-ink mb-1">Lever</h3>
            <p className="text-xs text-ink-faint leading-relaxed">
              Coming soon. Lever webhooks are not accepted yet, so do not configure a Lever webhook for Samastix.
            </p>
          </div>
        </div>
      </Section>

      <Section
        title="Candidate Feedback"
        subtitle="Personalised post-interview feedback emails for candidates"
      >
        <Toggle
          checked={sendCandidateFeedback}
          onChange={handleSendCandidateFeedbackChange}
          label="Send feedback to candidates after interview"
        />
        <Field label="Send after" hint="How long to wait after the interview before sending the email.">
          <select
            className={inputCls}
            value={feedbackDelayHours}
            disabled={feedbackDelaySaving || !sendCandidateFeedback}
            onChange={handleFeedbackDelayChange}
          >
            <option value={0}>Immediately (0h)</option>
            <option value={24}>24 hours</option>
            <option value={48}>48 hours (default)</option>
            <option value={72}>72 hours</option>
          </select>
        </Field>
        <p className="text-xs text-ink-faint leading-relaxed">
          Candidates receive a personalised feedback email with strengths and areas to improve.
          Risk scores and proctoring data are never included.
        </p>
      </Section>

      <Section
        title="Candidate Screening Threshold"
        subtitle="Candidates below this ATS score will be automatically rejected and will not be invited for interview. Set to 0 to invite everyone."
      >
        <form onSubmit={handleSaveMinAtsScore} className="space-y-4">
          <div className="rounded-xl border border-teal-100 bg-gradient-to-br from-teal-50/70 to-surface p-5 shadow-xs">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-semibold text-ink-faint uppercase tracking-wide mb-2">
                  Minimum ATS Score (%)
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  className={`${inputCls} font-mono text-2xl font-bold text-ink`}
                  style={{ maxWidth: '10rem' }}
                  value={minAtsScore}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setMinAtsScore(Number.isNaN(n) ? 0 : Math.min(100, Math.max(0, n)));
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={atsScoreSaving}
                className="btn btn-primary text-xs font-semibold px-5 py-2 disabled:opacity-60 h-[38px] shrink-0"
              >
                {atsScoreSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
            <p className="mt-4 text-sm text-ink-soft leading-relaxed">
              {minAtsScore === 0 ? (
                'All candidates will be invited regardless of resume score.'
              ) : minAtsScore <= 50 ? (
                'Low bar — most candidates will be invited.'
              ) : minAtsScore <= 75 ? (
                'Moderate — only reasonably qualified candidates invited.'
              ) : (
                'High bar — only strong matches will be invited.'
              )}
            </p>
          </div>
        </form>
      </Section>

      <Section
        title="Default Interview Delay"
        subtitle="How many hours after a candidate is invited before their interview is scheduled."
      >
        <form onSubmit={handleSaveInterviewDelay} className="flex flex-wrap items-end gap-3">
          <Field label="Hours">
            <input
              type="number"
              min={1}
              max={168}
              className={inputCls}
              style={{ maxWidth: '8rem' }}
              value={interviewDelay}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                setInterviewDelay(Number.isNaN(n) ? 24 : Math.min(168, Math.max(1, n)));
              }}
            />
          </Field>
          <button
            type="submit"
            disabled={delaySaving}
            className="btn btn-primary text-xs font-semibold px-5 py-2 disabled:opacity-60 h-[38px]"
          >
            {delaySaving ? 'Saving…' : 'Save'}
          </button>
        </form>
      </Section>

      <Section title="Webhooks" subtitle="Outbound HTTP integrations triggered on configured events">
        {loading ? <p className="text-xs text-slate-400">Loading…</p> : list.length === 0 ? (
          <p className="text-xs text-slate-400">No webhooks configured yet.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-faint border-b border-line">
                  <th className="py-2 px-2">Name</th>
                  <th className="py-2 px-2">URL</th>
                  <th className="py-2 px-2">Events</th>
                  <th className="py-2 px-2">Last Status</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((w) => (
                  <tr key={w.id} className="border-b border-line-soft last:border-0">
                    <td className="py-2 px-2 text-ink font-medium">{w.name}</td>
                    <td className="py-2 px-2 text-ink-faint font-mono text-xs truncate max-w-[200px]" title={w.url}>{w.url}</td>
                    <td className="py-2 px-2 text-ink-soft text-xs">{w.events}</td>
                    <td className="py-2 px-2 text-ink-soft font-mono text-xs">{w.lastStatus || '-'}</td>
                    <td className="py-2 px-2 text-right whitespace-nowrap">
                      <button onClick={() => handleTest(w.id)} disabled={busyId === w.id}
                        className="text-xs text-teal-600 font-semibold hover:underline mr-3 disabled:opacity-50">Test</button>
                      <button onClick={() => handleDelete(w.id)} disabled={busyId === w.id}
                        className="text-xs text-rose-600 hover:underline disabled:opacity-50">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <StatusMsg msg={msg} />
      </Section>

      <Section title="Add Webhook" subtitle="Register a new outbound integration">
        <form onSubmit={handleAdd} className="grid grid-cols-2 gap-4">
          <Field label="Name">
            <input className={inputCls} value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Slack notifier" required />
          </Field>
          <Field label="URL" hint="Must be an https:// URL on port 443 that is reachable on the public internet (private, local and cloud-metadata addresses are rejected).">
            <input type="url" className={inputCls} value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://hooks.slack.com/..." required />
          </Field>
          <Field label="Events" hint="Comma-separated event names (e.g. FINDING_CRITICAL, SESSION_ENDED)">
            <input className={inputCls} value={form.events}
              onChange={(e) => setForm((f) => ({ ...f, events: e.target.value }))} required />
          </Field>
          <Field label="Secret (optional)" hint="Used to sign each request: X-Samastix-Signature: sha256=<hex HMAC-SHA256 of the raw body>. Verify it on your side.">
            <input className={inputCls} value={form.secret}
              onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))} placeholder="leave blank for none" />
          </Field>
          <div className="col-span-2">
            <Toggle checked={form.enabled}
              onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} label="Enabled" />
            <button type="submit" disabled={adding}
              className="btn btn-primary text-xs font-semibold px-5 py-2 disabled:opacity-60 mt-3">
              {adding ? 'Adding…' : 'Add Webhook'}
            </button>
          </div>
        </form>
      </Section>
    </>
  );
}

// ===========================================================================
// Danger Zone
// ===========================================================================
function DangerZoneSection({ orgId }) {
  const [org,      setOrg]      = useState(null);
  const [confirm,  setConfirm]  = useState('');
  const [deleting, setDeleting] = useState(false);
  const [msg,      setMsg]      = useState(null);

  useEffect(() => {
    if (!orgId) return;
    getOrgById(orgId).then((res) => setOrg(res.data)).catch(() => {});
  }, [orgId]);

  async function handleDelete() {
    if (!org) return;
    if (confirm !== org.slug) {
      setMsg({ ok: false, text: 'Slug confirmation does not match.' }); return;
    }
    if (!window.confirm(`Permanently delete organization "${org.name}"? This cannot be undone.`)) return;
    setDeleting(true); setMsg(null);
    try {
      await deleteOrg(orgId, confirm);
      // Force reload to clear org-scoped state from the in-memory authStore.
      window.location.href = '/login';
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.message || 'Delete failed.' });
      setDeleting(false);
    }
  }

  if (!orgId) {
    return <Section title="Danger Zone" danger><p className="text-xs text-slate-400">No organization to delete.</p></Section>;
  }

  return (
    <Section title="Danger Zone" subtitle="Irreversible actions" danger>
      <p className="text-sm text-slate-700 mb-2">Delete this organization and all of its data.</p>
      <p className="text-xs text-slate-500 mb-4">
        Members, sessions, findings, schedules and webhooks will be permanently removed.
        Only the org owner can perform this action.
      </p>
      {org && (
        <Field label={`Type the org slug to confirm: ${org.slug}`}>
          <input className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={org.slug} />
        </Field>
      )}
      <StatusMsg msg={msg} />
      <button type="button" onClick={handleDelete} disabled={deleting || !org || confirm !== org?.slug}
        className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2 rounded-lg transition">
        {deleting ? 'Deleting…' : 'Delete Organization'}
      </button>
    </Section>
  );
}
